import { useEffect, useState } from "react";
import api from "../../../../lib/api";
import { FormField, EmptyState } from "../shared";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { toast } from "sonner";
import {
  Truck, Check, ArrowRight, Zap, FileText, Plug, Clock, AlertTriangle,
  ShieldCheck, ChevronRight, DollarSign, Users,
  Settings as SettingsIcon, FileSignature, Lock, Landmark, CalendarClock,
  Upload,
} from "lucide-react";

// 4. Terms (the moat) — merged tab: Your workflow (one-sided) + Signed
//     terms per buyer (bilateral, dual-sign).
// ─────────────────────────────────────────────────────────────────────
export default function TermsTab() {
  // List all connected buyers; pick one → edit terms inline.
  const [counterparts, setCounterparts] = useState([]);
  const [activeId, setActiveId] = useState(null);
  useEffect(() => {
    api.get("/buyers").then(r => {
      setCounterparts(r.data || []);
      if ((r.data || []).length && !activeId) setActiveId(r.data[0].id);
    }).catch(() => {});
    // eslint-disable-next-line
  }, []);
  return (
    <div className="space-y-8">
      {/* ─── Section 1: Info — buyer decides the workflow ─── */}
      <section>
        <div className="rounded-xl px-4 py-3 border border-border bg-foreground/[0.03] mb-4 flex items-start gap-2">
          <SettingsIcon className="w-4 h-4 mt-0.5 shrink-0 text-foreground/60"/>
          <div>
            <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground">Workflow mode</div>
            <div className="text-[12px] text-foreground/65 mt-0.5">
              <span className="font-medium">Your buyer decides whether each trading pair uses Full Auto or Daily Confirm.</span>
              {" "}You follow whatever they choose — the current mode per buyer is shown in the signed terms below.
            </div>
          </div>
        </div>
      </section>

      {/* ─── Section 2: Signed terms per buyer (bilateral, dual-sign) ─── */}
      <section>
        <div className="rounded-xl px-4 py-3 border-2 border-indigo-200 bg-indigo-50/40 mb-4 flex items-start gap-2" data-testid="signed-terms-section-header">
          <FileSignature className="w-4 h-4 mt-0.5 shrink-0 text-indigo-700"/>
          <div>
            <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-indigo-800">Signed terms per buyer</div>
            <div className="text-[12px] text-indigo-900/75 mt-0.5">
              Bilateral contract — both parties must sign. Returns, shipping delays, damaged orders, and custom terms with deduction rules. Claims auto-deduct from the daily invoice when a term is breached.
            </div>
          </div>
        </div>
        {counterparts.length === 0 ? (
          <EmptyState icon={Users} title="No connected buyers" body="Once a buyer connects, you can negotiate dropship terms with them here."/>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4">
            <aside className="rounded-2xl border border-border bg-white p-2 space-y-1 self-start">
              {counterparts.map(b => (
                <button
                  key={b.id}
                  onClick={() => setActiveId(b.id)}
                  data-testid={`terms-buyer-${b.id}`}
                  className={`w-full text-left px-3 py-2.5 rounded-xl flex items-center justify-between ${activeId === b.id ? "bg-secondary" : "hover:bg-secondary/50"}`}
                >
                  <div className="min-w-0">
                    <div className="text-[13px] font-medium truncate">{b.company || b.name}</div>
                    <div className="text-[11px] text-foreground/55 truncate">{b.email}</div>
                  </div>
                  <ChevronRight className="w-3.5 h-3.5 text-foreground/30"/>
                </button>
              ))}
            </aside>
            <section>{activeId && <TermsEditor counterpartId={activeId}/>}</section>
          </div>
        )}
      </section>
    </div>
  );
}

export const TERM_FIELDS = [
  { key: "returns_policy",         label: "Returns policy",        ph: "30-day return window. Buyer pays return shipping unless damaged." },
  { key: "shipping_delays_policy", label: "Shipping delays",       ph: "Order ships within 24h. Delays > 48h trigger a 10% credit." },
  { key: "damaged_orders_policy",  label: "Damaged orders",        ph: "100% refund + reship at no charge if photo evidence is provided within 7 days." },
  { key: "shipping_policy",        label: "Shipping policy",       ph: "Free ground for orders > $50. Expedited at cost. Carrier: UPS / FedEx auto-pick." },
];

export const CLAIM_KINDS = [
  { key: "late_delivery", label: "Late delivery" },
  { key: "damaged",       label: "Damaged" },
  { key: "wrong_item",    label: "Wrong item" },
  { key: "no_movement",   label: "No tracking movement" },
];

// ─────────────────────────────────────────────────────────────────────
// Shipping policy section — BILATERAL. Either party can propose changes;
// both must re-sign. Two linked fields:
//   1. `shipping_paid_by`: who pays for the label.
//      - seller → invoice line = $0 (seller baked it into wholesale).
//      - buyer  → actual carrier cost passes through.
//      - flat_rate → pair-level flat $/order (seller absorbs variance).
//   2. `shipping_sla_liability`: who gets the refund credit when the
//      carrier is late or loses the package. Defaults follow who pays but
//      either side can override (both must re-sign).
// ─────────────────────────────────────────────────────────────────────
function ShippingPolicySection({ t, role, update }) {
  const mode = t.shipping_paid_by || "buyer";
  const liability = t.shipping_sla_liability || ({
    seller: "seller", buyer: "buyer", flat_rate: "seller",
  })[mode];
  const flatRate = t.flat_shipping_rate ?? 0;
  const MODES = [
    {
      k: "seller", icon: ShieldCheck,
      title: "Seller pays shipping",
      sub: "Label cost is baked into the wholesale price. Invoice shows shipping as Included.",
    },
    {
      k: "buyer", icon: Truck,
      title: "Buyer pays · actual cost passes through",
      sub: "Carrier label cost appears as a line on the daily invoice. Most common in wholesale.",
    },
    {
      k: "flat_rate", icon: DollarSign,
      title: "Flat rate per order",
      sub: "Fixed fee regardless of actual carrier cost. Seller absorbs the variance.",
    },
  ];
  const LIABILITIES = [
    { k: "seller", title: "Seller liable", sub: "Seller absorbs carrier failures — credit rolls onto buyer invoice." },
    { k: "buyer",  title: "Buyer liable",  sub: "Buyer accepts the risk. No credit issued when the carrier misses SLA." },
    { k: "shared", title: "Shared 50 / 50", sub: "Carrier-fault credit split evenly between buyer and seller." },
  ];
  return (
    <div className="rounded-xl border border-border bg-white p-4 space-y-3" data-testid="shipping-policy-section">
      <div>
        <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Shipping</div>
        <div className="text-[12.5px] text-foreground/70 mt-0.5 max-w-xl">
          Who pays for the shipping label on each order — and who's liable when the carrier is late or loses a package. Either side can propose changes; both must re-sign. verqo always brokers carrier claims on your behalf.
        </div>
      </div>

      <div className="space-y-2">
        {MODES.map(m => {
          const active = mode === m.k;
          const Icon = m.icon;
          return (
            <button key={m.k} type="button"
              onClick={() => update("shipping_paid_by", m.k)}
              data-testid={`shipping-paid-by-${m.k}`}
              className={`w-full text-left rounded-xl border p-3.5 transition-all flex items-start gap-3 ${
                active ? "border-foreground bg-foreground text-background" :
                            "border-border bg-white hover:border-foreground/40"
              }`}>
              <Icon className={`w-5 h-5 shrink-0 mt-0.5 ${active ? "text-background" : "text-foreground/65"}`}/>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-[13px] font-medium">{m.title}</div>
                  {active && <span className="ml-auto text-[9.5px] mono uppercase tracking-[0.16em] px-1.5 py-0.5 rounded-full bg-background/20">Selected</span>}
                </div>
                <div className={`text-[11.5px] mt-1 ${active ? "text-background/70" : "text-foreground/60"}`}>{m.sub}</div>
              </div>
            </button>
          );
        })}
      </div>

      {mode === "flat_rate" && (
        <div className="grid grid-cols-[1fr_200px] gap-3 items-center pt-2 border-t border-border">
          <div>
            <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Flat rate per order</div>
            <div className="text-[12px] text-foreground/65 mt-0.5">Fixed dollar amount the buyer pays per order regardless of carrier cost.</div>
          </div>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-foreground/55">$</span>
            <Input type="number" min="0" step="0.01" value={flatRate}
              onChange={e => update("flat_shipping_rate", e.target.value === "" ? 0 : Number(e.target.value))}
              data-testid="flat-shipping-rate"
              className="h-11 pl-6 pr-14 mono text-right"/>
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] mono text-foreground/55">/order</span>
          </div>
        </div>
      )}

      {/* Carrier-fault liability */}
      <div className="pt-3 border-t border-border space-y-2">
        <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">
          Carrier-fault liability · who gets the credit
        </div>
        <div className="text-[11.5px] text-foreground/60 max-w-xl">
          When the carrier is late / loses a package, verqo files the carrier claim automatically. This setting decides who the refund credit goes to.
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {LIABILITIES.map(l => {
            const active = liability === l.k;
            return (
              <button key={l.k} type="button"
                onClick={() => update("shipping_sla_liability", l.k)}
                data-testid={`shipping-liability-${l.k}`}
                className={`text-left rounded-xl border p-3 transition-all ${
                  active ? "border-foreground bg-foreground text-background" :
                              "border-border bg-white hover:border-foreground/40"
                }`}>
                <div className="text-[12.5px] font-medium">{l.title}</div>
                <div className={`text-[11px] mt-1 ${active ? "text-background/70" : "text-foreground/60"}`}>{l.sub}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="rounded-xl border border-border bg-secondary/50 p-3 text-[11.5px] text-foreground/75 flex gap-2">
        <Truck className="w-4 h-4 shrink-0 mt-0.5 text-foreground/60"/>
        <div>
          <strong>verqo brokers all carrier claims.</strong> Neither side opens claims with the carrier directly — the platform files on the seller's shipping account and routes the refund per this liability setting.
        </div>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Platform fee section — bilateral. Either party can flip it; both must
// re-sign. Default: seller pays. Rate is fixed at the platform default
// (3%, admin-overridable). Only WHO PAYS is negotiable here.
// ─────────────────────────────────────────────────────────────────────
function PlatformFeeSection({ t, role, update }) {
  const mode = t.platform_fee_paid_by || "seller";
  const pct = 3.0;  // Display the platform default. Backend recomputes per-seller at invoice time.
  const MODES = [
    {
      k: "seller", icon: ShieldCheck,
      title: "Seller pays the 3% platform fee",
      sub: "Auto-deducted from seller's invoice payout. Buyer's bill stays the same. (Default)",
    },
    {
      k: "buyer", icon: DollarSign,
      title: "Buyer pays the 3% platform fee",
      sub: "Added as a separate line on the daily invoice. Seller receives the full goods value.",
    },
  ];
  return (
    <div className="rounded-xl border border-border bg-white p-4 space-y-3" data-testid="platform-fee-section">
      <div>
        <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Platform fee</div>
        <div className="text-[12.5px] text-foreground/70 mt-0.5 max-w-xl">
          verqo's commission is a flat <strong>{pct}%</strong> per daily invoice. Either side can choose to absorb it — both must re-sign after a change.
        </div>
      </div>

      <div className="space-y-2">
        {MODES.map(m => {
          const active = mode === m.k;
          const Icon = m.icon;
          return (
            <button key={m.k} type="button"
              onClick={() => update("platform_fee_paid_by", m.k)}
              data-testid={`platform-fee-paid-by-${m.k}`}
              className={`w-full text-left rounded-xl border p-3.5 transition-all flex items-start gap-3 ${
                active
                  ? "border-foreground bg-foreground text-background"
                  : "border-border bg-white hover:border-foreground/40"
              }`}>
              <Icon className={`w-5 h-5 shrink-0 mt-0.5 ${active ? "text-background" : "text-foreground/65"}`}/>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-[13px] font-medium">{m.title}</div>
                  {active && <span className="ml-auto text-[9.5px] mono uppercase tracking-[0.16em] px-1.5 py-0.5 rounded-full bg-background/20">Selected</span>}
                </div>
                <div className={`text-[11.5px] mt-1 ${active ? "text-background/70" : "text-foreground/60"}`}>{m.sub}</div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="rounded-xl border border-border bg-secondary/50 p-3 text-[11.5px] text-foreground/75 flex gap-2">
        <DollarSign className="w-4 h-4 shrink-0 mt-0.5 text-foreground/60"/>
        <div>
          The fee is automatic and irrevocable per invoice — once an invoice is generated, its rate and split are locked. Future invoices use the latest setting.
        </div>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Payment policy section — buyer-owned, nested inside TermsEditor.
// Only the buyer can edit; seller sees it read-only.
// Policies:
//   • autopay_on_due_date — Net-30 ACH autopay. Requires active credit line.
//   • prepay_per_order   — ACH charged per order before seller ships.
//   • manual_per_invoice — buyer pays each invoice manually (default).
// ─────────────────────────────────────────────────────────────────────
function PaymentPolicySection({ t, role, posture, paymentMethods, update }) {
  const policy = t.payment_policy || "manual_per_invoice";
  const hasCredit = !!(posture?.protections || []).find(
    p => p.source === "net_terms" && p.active
  );
  // Only ACH-capable methods are valid for autopay.
  const bankMethods = (paymentMethods || []).filter(m => m.type === "bank");
  // Payment policy is SELLER-owned — the seller decides which settlement
  // methods they accept. Buyers see it read-only. (Autopay method is still
  // the buyer's ACH pick though, so we keep that field editable when the
  // buyer is viewing.)
  const readOnly = role !== "seller";
  const POLICIES = [
    {
      k: "autopay_on_due_date",
      icon: CalendarClock,
      title: "Autopay on due date · ACH",
      sub: "Daily invoices auto-debit via ACH on the due date. Prepay option always available.",
      disabled: !hasCredit,
      badge: hasCredit ? null : "Locked · needs credit",
    },
    {
      k: "prepay_per_order",
      icon: Zap,
      title: "Prepay per order · ACH / wallet",
      sub: "Each order charges the buyer's default payment method before shipping. Ship only if charge approves.",
    },
    {
      k: "manual_per_invoice",
      icon: FileSignature,
      title: "Manual — pay each invoice",
      sub: "Buyer reviews and pays each daily invoice manually. Use only for low-volume relationships.",
    },
  ];
  return (
    <div className="rounded-xl border border-border bg-white p-4 space-y-3" data-testid="payment-policy-section">
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Payment</div>
          <div className="text-[12.5px] text-foreground/70 mt-0.5 max-w-xl">
            {role === "seller"
              ? "Which settlement method do you require from this buyer? Autopay keeps the relationship frictionless once credit is active."
              : "The seller sets the payment policy for this relationship. Pick your ACH method below when autopay is enabled."}
          </div>
        </div>
      </div>
      <div className="space-y-2">
        {POLICIES.map(opt => {
          const active = policy === opt.k;
          const Icon = opt.icon;
          const disabled = readOnly || opt.disabled;
          return (
            <button key={opt.k} type="button"
              onClick={() => { if (!disabled) update("payment_policy", opt.k); }}
              disabled={disabled}
              data-testid={`payment-policy-${opt.k}`}
              className={`w-full text-left rounded-xl border p-3.5 transition-all flex items-start gap-3 ${
                active ? "border-foreground bg-foreground text-background" :
                disabled ? "border-border bg-secondary/40 opacity-60 cursor-not-allowed" :
                            "border-border bg-white hover:border-foreground/40"
              }`}>
              <Icon className={`w-5 h-5 shrink-0 mt-0.5 ${active ? "text-background" : "text-foreground/65"}`}/>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-[13px] font-medium">{opt.title}</div>
                  {active && <span className="ml-auto text-[9.5px] mono uppercase tracking-[0.16em] px-1.5 py-0.5 rounded-full bg-background/20">Selected</span>}
                  {opt.badge && (
                    <span className="text-[9.5px] mono uppercase tracking-[0.16em] px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200 inline-flex items-center gap-1">
                      <Lock className="w-2.5 h-2.5"/>{opt.badge}
                    </span>
                  )}
                </div>
                <div className={`text-[11.5px] mt-1 ${active ? "text-background/70" : "text-foreground/60"}`}>{opt.sub}</div>
              </div>
            </button>
          );
        })}
      </div>

      {policy !== "manual_per_invoice" && (
        <div className="pt-3 border-t border-border space-y-2" data-testid="autopay-method-picker">
          <div className="flex items-center justify-between">
            <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Autopay method for this supplier</div>
            <a href="/buyer/payments/methods"
               className="text-[11px] text-foreground/55 underline hover:text-foreground inline-flex items-center gap-1">
              Manage methods<ArrowRight className="w-3 h-3"/>
            </a>
          </div>
          {bankMethods.length === 0 ? (
            <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-[12.5px] text-amber-900 flex gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5"/>
              <div>
                <strong>No bank accounts on file.</strong> Add an ACH bank account in the <a href="/buyer/payments/methods" className="underline">Payments portal</a> before enabling this policy.
              </div>
            </div>
          ) : (
            <div className="relative">
              <select
                value={t.autopay_method_id || ""}
                disabled={role !== "buyer"}
                onChange={e => update("autopay_method_id", e.target.value || null)}
                data-testid="autopay-method-select"
                className="w-full h-11 rounded-lg border border-border bg-white pl-10 pr-9 text-[13px] appearance-none focus:outline-none focus:ring-2 focus:ring-foreground/10 disabled:bg-secondary/40 disabled:opacity-80">
                <option value="">— Pick an ACH bank account —</option>
                {bankMethods.map(m => (
                  <option key={m.id} value={m.id}>
                    {(m.bank_name || m.label || "Bank")} · ····{m.last4 || "****"}
                    {m.is_default ? " (default)" : ""}
                  </option>
                ))}
              </select>
              <Landmark className="w-4 h-4 text-foreground/55 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"/>
              <ChevronRight className="w-4 h-4 text-foreground/55 absolute right-3 top-1/2 -translate-y-1/2 rotate-90 pointer-events-none"/>
            </div>
          )}
          <div className="text-[11px] text-foreground/55">
            Only ACH bank accounts from the buyer's <a href="/buyer/payments/methods" className="underline hover:text-foreground">saved payment methods</a> can be used for autopay. Cards aren't eligible.
          </div>
        </div>
      )}

      {policy === "autopay_on_due_date" && !hasCredit && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-[12.5px] text-amber-900 flex gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5"/>
          <div>
            <strong>Autopay requires an approved credit line.</strong> The buyer needs Net-30 with you before saving, or choose <em>Prepay per order</em>.
          </div>
        </div>
      )}
      {policy === "prepay_per_order" && (
        <div className="rounded-xl border border-border bg-secondary/50 p-3 text-[12.5px] text-foreground/75">
          Each order charges the buyer's ACH method before you can ship. Typical settle time: <strong>1–2 business days</strong>. If ACH fails, the order waits in <em>Awaiting payment</em>.
        </div>
      )}
      {role === "buyer" && (
        <div className="text-[11px] mono uppercase tracking-[0.14em] text-foreground/50 pt-1 border-t border-border">
          Read-only · seller sets the payment policy
        </div>
      )}
    </div>
  );
}


// Helper to render an amendment diff value compactly. Renders objects (e.g.
// `responsibilities`, `return_address`) as JSON, primitives as-is, and
// nullish as a soft dash.
function formatDiffValue(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "object") {
    try { return JSON.stringify(v); } catch { return String(v); }
  }
  return String(v);
}

export function TermsEditor({ counterpartId, role = "seller", onSaved }) {
  const [t, setT] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [posture, setPosture] = useState(null);
  // Amendment mode — when the agreement is locked and the user clicks
  // "Propose change", we open the form for editing but route the next
  // save through POST /propose-amendment instead of PUT. Cancelling
  // reverts the local edits to the server state.
  const [amendMode, setAmendMode] = useState(false);
  const [amendNote, setAmendNote] = useState("");
  // Payment methods — only loaded for the buyer side so they can pick their
  // own ACH method for autopay. Filtered to `type=bank` in the dropdown.
  const [paymentMethods, setPaymentMethods] = useState([]);
  const load = () => api.get(`/dropship/terms/${counterpartId}`).then(r => { setT(r.data); setAmendMode(false); setAmendNote(""); }).catch(() => {});
  const loadPosture = () => api.get(`/dropship/credit-posture/${counterpartId}`)
    .then(r => setPosture(r.data)).catch(() => {});
  const loadMethods = () => {
    if (role !== "buyer") return;
    api.get("/payment-methods").then(r => setPaymentMethods(r.data || [])).catch(() => {});
  };
  useEffect(() => { load(); loadPosture(); loadMethods(); /* eslint-disable-next-line */ }, [counterpartId]);

  // ── File upload helpers ─────────────────────────────────────────────
  const onContractFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";  // allow re-picking the same file later
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large — 10 MB max"); return;
    }
    const b64 = await new Promise((res, rej) => {
      const reader = new FileReader();
      reader.onload = () => {
        const r = String(reader.result || "");
        // Strip the "data:...;base64," prefix.
        const idx = r.indexOf("base64,");
        res(idx >= 0 ? r.slice(idx + 7) : r);
      };
      reader.onerror = rej;
      reader.readAsDataURL(file);
    });
    setUploading(true);
    try {
      await api.post(`/dropship/terms/${counterpartId}/contract`, {
        file_name: file.name,
        file_type: file.type || "application/pdf",
        file_data_b64: b64,
      });
      toast.success("Contract attached — terms marked legally executed");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };
  const downloadContract = async () => {
    try {
      const { data } = await api.get(`/dropship/terms/${counterpartId}/contract`);
      const bin = atob(data.file_data_b64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const blob = new Blob([bytes], { type: data.file_type });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = data.file_name; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(err.response?.data?.detail || "Download failed"); }
  };
  const removeContract = async () => {
    if (!window.confirm("Remove the attached contract? Terms remain on file but lose their legally-executed status.")) return;
    try { await api.delete(`/dropship/terms/${counterpartId}/contract`); toast.success("Contract removed"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Remove failed"); }
  };

  if (!t) return <div className="rounded-2xl border border-border bg-white p-6 text-foreground/50 text-[13px]">Loading…</div>;

  const update = (k, v) => setT({ ...t, [k]: v });
  const addCustom = () => setT({ ...t, custom_terms: [...(t.custom_terms || []), { label: "", body: "" }] });
  const updCustom = (i, k, v) => {
    const arr = [...(t.custom_terms || [])];
    arr[i] = { ...arr[i], [k]: v };
    setT({ ...t, custom_terms: arr });
  };
  const removeCustom = (i) => setT({ ...t, custom_terms: (t.custom_terms || []).filter((_, idx) => idx !== i) });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        counterpart_id: counterpartId,
        returns_policy: t.returns_policy,
        shipping_delays_policy: t.shipping_delays_policy,
        damaged_orders_policy: t.damaged_orders_policy,
        shipping_policy: t.shipping_policy,
        custom_terms: t.custom_terms,
        responsibilities: t.responsibilities,
        shipping_sla_hours: t.shipping_sla_hours ? Number(t.shipping_sla_hours) : 48,
        returns_handler: t.returns_handler || "buyer_handles",
        return_window_days: t.return_window_days ? Number(t.return_window_days) : 30,
        return_address: t.return_address || null,
      };
      // Buyer-owned field — only include when the editor is buyer-facing.
      if (role === "buyer" && t.automation_mode) payload.automation_mode = t.automation_mode;
      // Shipping policy — now bilateral. Either role can update it and
      // both must re-sign, same pattern as platform fee. Always include.
      payload.shipping_paid_by = t.shipping_paid_by || "buyer";
      payload.flat_shipping_rate = t.shipping_paid_by === "flat_rate"
        ? Number(t.flat_shipping_rate || 0)
        : 0;
      payload.shipping_sla_liability = t.shipping_sla_liability || null;
      // Payment policy — now SELLER-owned. The seller picks the policy;
      // the buyer picks their autopay ACH method. So the seller sends the
      // policy, and the buyer sends the method id.
      if (role === "seller") {
        payload.payment_policy = t.payment_policy || "manual_per_invoice";
      }
      if (role === "buyer") {
        payload.autopay_method_id = t.autopay_method_id || null;
      }
      // Platform fee — bilateral, either side can change. Always include.
      payload.platform_fee_paid_by = t.platform_fee_paid_by || "seller";
      if (amendMode) {
        // Locked agreement → route through the amendment endpoint. Server
        // diffs against the active doc and stores only the changed fields.
        const { counterpart_id: _drop, ...amendBody } = payload;  // eslint-disable-line no-unused-vars
        amendBody.proposal_note = amendNote || null;
        await api.post(`/dropship/terms/${counterpartId}/propose-amendment`, amendBody);
        toast.success("Amendment proposed · awaiting counterpart's signature");
      } else {
        await api.put(`/dropship/terms/${counterpartId}`, payload);
        toast.success("Saved · both sides must re-sign");
      }
      load();
      if (onSaved) onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };
  const sign = async () => {
    try { await api.post(`/dropship/terms/${counterpartId}/sign`); toast.success("Signed"); load(); if (onSaved) onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not sign"); }
  };
  const acceptAmendment = async () => {
    try {
      const { data } = await api.post(`/dropship/terms/${counterpartId}/accept-amendment`);
      toast.success(`Amendment accepted · now on v${data.version}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not accept"); }
  };
  const declineAmendment = async () => {
    if (!window.confirm("Decline this amendment? The active agreement stays unchanged.")) return;
    try { await api.post(`/dropship/terms/${counterpartId}/decline-amendment`); toast.success("Amendment declined"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not decline"); }
  };
  const withdrawAmendment = async () => {
    if (!window.confirm("Withdraw your proposal? You can re-propose anytime.")) return;
    try { await api.post(`/dropship/terms/${counterpartId}/decline-amendment`); toast.success("Amendment withdrawn"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not withdraw"); }
  };

  const isActive = t.status === "active";
  const isLocked = !!t.locked || isActive;
  const pending  = t.pending_amendment;
  const youProposed = !!(pending && pending.proposer_role === role);
  const counterpartProposed = !!(pending && pending.proposer_role !== role);
  // Inputs are editable when (a) we're still drafting, OR (b) we're in
  // active "Propose amendment" mode. Anything else is read-only.
  const fieldsReadOnly = isLocked && !amendMode;
  const youSigned  = role === "buyer" ? !!t.buyer_signed_at  : !!t.seller_signed_at;
  const themSigned = role === "buyer" ? !!t.seller_signed_at : !!t.buyer_signed_at;
  const youLabel   = "You";
  const themLabel  = role === "buyer" ? "Seller" : "Buyer";
  const signLabel  = role === "buyer" ? "Sign as buyer" : "Sign as supplier";
  const contract = t.attached_contract;
  const fileSizeKb = contract ? (contract.size_bytes / 1024) : 0;

  return (
    <div className="rounded-2xl border border-border bg-white p-6 space-y-5" data-testid="terms-editor">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Status</div>
          <div className="display text-xl mt-0.5 inline-flex items-center gap-2">
            {isActive ? <span className="text-emerald-700">Active</span> : <span>{t.status === "draft" ? "Draft" : t.status}</span>}
            <span className="text-[11px] mono text-foreground/45">v{t.version}</span>
            {isLocked && (
              <span data-testid="terms-locked-chip" className="text-[10.5px] mono uppercase tracking-[0.16em] px-2 py-0.5 rounded-full bg-foreground/[0.06] text-foreground/70 border border-foreground/15">
                <Lock className="w-3 h-3 inline mr-1"/>Locked
              </span>
            )}
            {t.legally_executed && (
              <span className="text-[10.5px] mono uppercase tracking-[0.16em] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                <ShieldCheck className="w-3 h-3 inline mr-1"/>Legally executed
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2 text-[11.5px] mono uppercase tracking-[0.16em]">
          <span className={`px-2 py-1 rounded-full border ${themSigned ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-secondary text-foreground/55 border-border"}`}>{themSigned ? `${themLabel} signed ✓` : `${themLabel} pending`}</span>
          <span className={`px-2 py-1 rounded-full border ${youSigned ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-secondary text-foreground/55 border-border"}`}>{youSigned ? `${youLabel} signed ✓` : `${youLabel} pending`}</span>
        </div>
      </div>

      {/* ── Lock & amendment banner ─────────────────────────────────────────
          When the agreement is active (both signed), it becomes locked —
          direct edits blocked. Any change must be proposed as an amendment
          and counter-signed. Banner morphs across 4 states:
            1. counterpart proposed → diff + Accept/Decline CTAs
            2. you proposed         → "Awaiting counter-signature" + Withdraw
            3. locked, amendMode on → "Editing amendment proposal" + Cancel
            4. locked, amendMode off→ "Agreement locked" + Propose change
      */}
      {counterpartProposed && (
        <div className="rounded-2xl border border-amber-300 bg-amber-50/60 px-5 py-4 space-y-3" data-testid="amendment-incoming-banner">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl grid place-items-center bg-amber-500 text-white shrink-0">
              <FileSignature className="w-5 h-5"/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13.5px] font-semibold text-amber-950">
                {themLabel} proposed an amendment · {Object.keys(pending.diff || {}).length} change{Object.keys(pending.diff || {}).length === 1 ? "" : "s"}
              </div>
              {pending.note && (
                <div className="text-[12px] text-amber-900/85 mt-1 italic">&ldquo;{pending.note}&rdquo;</div>
              )}
              <div className="mt-3 rounded-lg bg-white border border-amber-200 divide-y divide-amber-100 overflow-hidden" data-testid="amendment-diff-list">
                {Object.entries(pending.diff || {}).map(([k, v]) => (
                  <div key={k} className="grid grid-cols-[1fr_2fr] gap-3 px-3 py-2 text-[12px]">
                    <div className="mono uppercase tracking-[0.14em] text-foreground/55 text-[10.5px] pt-0.5">{k.replaceAll("_", " ")}</div>
                    <div className="space-y-0.5">
                      <div className="text-rose-700 line-through truncate">{formatDiffValue(v.before)}</div>
                      <div className="text-emerald-700 font-medium truncate">{formatDiffValue(v.after)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <Button onClick={acceptAmendment} data-testid="accept-amendment" className="rounded-full bg-emerald-600 text-white hover:bg-emerald-700 h-9 px-5">
              <Check className="w-4 h-4 mr-1"/>Accept &amp; sign
            </Button>
            <Button onClick={declineAmendment} data-testid="decline-amendment" variant="outline" className="rounded-full h-9 px-5">
              Decline
            </Button>
          </div>
        </div>
      )}
      {youProposed && (
        <div className="rounded-2xl border border-sky-300 bg-sky-50/60 px-5 py-3.5 flex items-start gap-3" data-testid="amendment-outgoing-banner">
          <div className="w-10 h-10 rounded-xl grid place-items-center bg-sky-500 text-white shrink-0">
            <Clock className="w-5 h-5"/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-medium text-sky-950">Your amendment is awaiting {themLabel.toLowerCase()}&apos;s counter-signature</div>
            <div className="text-[11.5px] text-sky-900/75 mt-0.5">
              {Object.keys(pending.diff || {}).length} field{Object.keys(pending.diff || {}).length === 1 ? "" : "s"} proposed · v{t.version} stays active until they accept.
            </div>
          </div>
          <Button onClick={withdrawAmendment} variant="outline" size="sm" data-testid="withdraw-amendment" className="rounded-full h-8 px-4 text-[11.5px] shrink-0">
            Withdraw
          </Button>
        </div>
      )}
      {isLocked && !pending && !amendMode && (
        <div className="rounded-2xl border border-foreground/15 bg-foreground/[0.025] px-5 py-3.5 flex items-start gap-3" data-testid="agreement-locked-banner">
          <div className="w-10 h-10 rounded-xl grid place-items-center bg-foreground/10 shrink-0">
            <Lock className="w-5 h-5 text-foreground/70"/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-medium">Agreement locked at v{t.version}</div>
            <div className="text-[11.5px] text-foreground/65 mt-0.5">
              Both parties signed — direct edits are disabled. Propose a change and {themLabel.toLowerCase()} will counter-sign before it goes live.
            </div>
          </div>
          <Button onClick={() => setAmendMode(true)} variant="outline" data-testid="propose-amendment-btn" className="rounded-full h-9 px-4 shrink-0">
            <FileSignature className="w-4 h-4 mr-1"/>Propose change
          </Button>
        </div>
      )}
      {isLocked && amendMode && (
        <div className="rounded-2xl border-2 border-dashed border-amber-400 bg-amber-50/40 px-5 py-3.5 space-y-3" data-testid="amendment-mode-banner">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl grid place-items-center bg-amber-500 text-white shrink-0">
              <FileSignature className="w-5 h-5"/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-medium text-amber-950">Editing amendment proposal</div>
              <div className="text-[11.5px] text-amber-900/75 mt-0.5">
                Changes save as a proposal · {themLabel.toLowerCase()} must counter-sign before they go live. The current v{t.version} stays active until then.
              </div>
            </div>
            <Button onClick={() => { setAmendMode(false); setAmendNote(""); load(); }} variant="outline" size="sm" data-testid="cancel-amendment" className="rounded-full h-8 px-4 text-[11.5px] shrink-0">
              Cancel
            </Button>
          </div>
          <textarea
            value={amendNote}
            onChange={e => setAmendNote(e.target.value.slice(0, 500))}
            placeholder="Why are you proposing this change? (shown to the counterpart — optional)"
            rows={2}
            data-testid="amendment-note"
            className="w-full rounded-md border border-amber-300 bg-white p-2.5 text-[12.5px] resize-none focus:outline-none focus:ring-2 focus:ring-amber-300/40"
          />
        </div>
      )}

      {/* ── Form fields — wrapped to disable interaction when the agreement
          is locked AND we're not in amend mode. Inputs stay visible (so
          the user can still read the active clause set) but unclickable. */}
      <div
        className={fieldsReadOnly ? "pointer-events-none opacity-60 select-none space-y-5" : "space-y-5"}
        aria-disabled={fieldsReadOnly}
        data-testid="terms-fields-wrap"
      >

      {/* ── Automation mode (buyer-owned; seller sees read-only) ─────────── */}
      {role === "buyer" ? (
        <div className="rounded-xl border border-border bg-secondary/20 p-4" data-testid="terms-automation-mode-edit">
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Workflow mode · you decide</div>
              <div className="text-[12px] text-foreground/65 mt-0.5">Pick how orders flow with this supplier. The seller follows whatever you choose.</div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {[
              { k: "full_auto",     l: "Full Auto",     icon: Zap,   sub: "Orders auto-route to fulfilment, ship same day. Daily invoice at midnight. Requires Net-30 or prepay." },
              { k: "daily_confirm", l: "Daily Confirm", icon: Clock, sub: "You review each day's orders before ship. 11 AM EST cutoff = same-day ship. No credit required." },
            ].map(opt => {
              const Icon = opt.icon;
              const active = (t.automation_mode || "daily_confirm") === opt.k;
              const fullAutoBlocked = opt.k === "full_auto" && posture && !posture.full_auto_allowed;
              const disabled = fullAutoBlocked;
              return (
                <button key={opt.k}
                  type="button"
                  onClick={() => !disabled && setT({ ...t, automation_mode: opt.k })}
                  disabled={disabled}
                  data-testid={`mode-${opt.k}`}
                  title={disabled ? "Full Auto requires credit, insurance, or wallet ≥ $1,000" : undefined}
                  className={`text-left rounded-xl border p-3 transition-all ${active ? "border-foreground bg-foreground text-background" : disabled ? "border-border bg-secondary/40 opacity-60 cursor-not-allowed" : "border-border bg-white hover:border-foreground/40"}`}>
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4"/>
                    <div className="font-medium text-[13px]">{opt.l}</div>
                    {active && <span className="ml-auto text-[9.5px] mono uppercase tracking-[0.16em] px-1.5 py-0.5 rounded-full bg-background/20">Selected</span>}
                    {fullAutoBlocked && <span className="ml-auto text-[9.5px] mono uppercase tracking-[0.16em] px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200 inline-flex items-center gap-1"><Lock className="w-2.5 h-2.5"/>Locked</span>}
                  </div>
                  <div className={`text-[11.5px] mt-1.5 ${active ? "text-background/70" : "text-foreground/60"}`}>{opt.sub}</div>
                </button>
              );
            })}
          </div>
          {/* Posture detail — shows exactly why Full Auto is allowed or not */}
          {posture && (
            <div className="mt-3 rounded-lg border border-border bg-white p-3" data-testid="credit-posture-buyer">
              <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/55 mb-2">
                {posture.full_auto_allowed ? "🛡  Full Auto is covered by" : "⚠  To enable Full Auto you need one of"}
              </div>
              <div className="space-y-1">
                {posture.protections.map((p) => (
                  <div key={p.source} className="flex items-center gap-2 text-[12px]">
                    <span className={`w-1.5 h-1.5 rounded-full ${p.active ? "bg-emerald-500" : "bg-foreground/20"}`}/>
                    <span className={p.active ? "text-foreground" : "text-foreground/50 line-through"}>{p.label}</span>
                  </div>
                ))}
              </div>
              {!posture.full_auto_allowed && (
                <div className="mt-2 flex gap-2 flex-wrap">
                  <a href="/payments/wallet" className="text-[11px] mono uppercase tracking-[0.14em] px-2 py-1 rounded-full border border-border hover:bg-secondary" data-testid="posture-top-up-link">Top up wallet</a>
                  <a href="/credit-apps" className="text-[11px] mono uppercase tracking-[0.14em] px-2 py-1 rounded-full border border-border hover:bg-secondary" data-testid="posture-credit-link">Apply for credit</a>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-secondary/30 p-4" data-testid="terms-automation-mode">
          <div className="flex items-start gap-3">
            {t.automation_mode === "full_auto" ? <Zap className="w-5 h-5 text-foreground shrink-0 mt-0.5"/> : <Clock className="w-5 h-5 text-foreground shrink-0 mt-0.5"/>}
            <div className="min-w-0">
              <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Workflow mode · chosen by buyer</div>
              <div className="display text-lg mt-0.5">
                {t.automation_mode === "full_auto" ? "Full Auto" : "Daily Confirm"}
              </div>
              <div className="text-[12px] text-foreground/65 mt-0.5">
                {t.automation_mode === "full_auto"
                  ? "Orders auto-route to fulfilment — ship same-day. Daily invoice at midnight EST."
                  : "Buyer reviews and confirms each day's orders. 11 AM EST cutoff = same-day ship."}
              </div>
            </div>
            <span className="ml-auto text-[10.5px] mono uppercase tracking-[0.16em] px-2 py-0.5 rounded-full bg-foreground/5 border border-border text-foreground/55 shrink-0 self-start flex items-center gap-1">
              <Lock className="w-2.5 h-2.5"/>Read-only for you
            </span>
          </div>
          {/* Seller-side "your shipments are protected by" — transparency into the guardrail */}
          {t.automation_mode === "full_auto" && posture && (
            <div className="mt-3 pt-3 border-t border-border">
              <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/55 mb-2">🛡  Your shipments are protected by</div>
              <div className="space-y-1">
                {posture.protections.filter(p => p.active).map(p => (
                  <div key={p.source} className="flex items-center gap-2 text-[12px]">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"/>
                    <span>{p.label}</span>
                  </div>
                ))}
                {posture.protections.filter(p => p.active).length === 0 && (
                  <div className="text-[12px] text-rose-700">⚠ No active protection — contact admin.</div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Existing-contract attachment ─────────────────────────── */}
      <div className="rounded-xl border border-foreground/10 bg-secondary/20 p-4">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div>
            <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Existing contract</div>
            <div className="text-[12.5px] text-foreground/70 mt-0.5 max-w-xl">
              Already have a signed MSA, DocuSign agreement, or scanned contract? Upload it here as the legal source of truth — the structured rules below stay useful as machine-readable deduction triggers.
            </div>
          </div>
          {!contract && (
            <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 h-9 rounded-full bg-foreground text-background hover:bg-foreground/90 text-[12.5px] whitespace-nowrap" data-testid="upload-contract-btn">
              <input
                type="file"
                accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/png,image/jpeg"
                onChange={onContractFile}
                disabled={uploading}
                className="hidden"
                data-testid="upload-contract-input"
              />
              {uploading ? "Uploading…" : <><Plug className="w-3.5 h-3.5"/>Upload contract</>}
            </label>
          )}
        </div>
        {contract && (
          <div className="rounded-xl border border-border bg-white p-3 flex items-center gap-3 mt-2" data-testid="attached-contract">
            <div className="w-10 h-10 rounded-xl bg-secondary border border-border grid place-items-center shrink-0">
              <FileText className="w-4 h-4 text-foreground/55"/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13.5px] font-medium truncate">{contract.file_name}</div>
              <div className="text-[11px] text-foreground/55 truncate">
                {contract.file_type} · {fileSizeKb < 1024 ? `${fileSizeKb.toFixed(1)} KB` : `${(fileSizeKb / 1024).toFixed(2)} MB`}
                {" · "}Uploaded by {contract.uploaded_by_role} · {new Date(contract.uploaded_at).toLocaleDateString()}
              </div>
              {contract.note && <div className="text-[11px] text-foreground/65 mt-0.5 italic">"{contract.note}"</div>}
            </div>
            <div className="flex gap-1 shrink-0">
              <Button onClick={downloadContract} variant="outline" className="h-8 rounded-full text-[12px] px-3" data-testid="download-contract">Download</Button>
              <Button onClick={removeContract} variant="outline" className="h-8 rounded-full text-[12px] px-3 border-rose-200 text-rose-700 hover:bg-rose-50" data-testid="remove-contract">Remove</Button>
              <label className="cursor-pointer inline-flex items-center px-3 h-8 rounded-full border border-border hover:bg-secondary text-[12px]">
                <input type="file" accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/png,image/jpeg" onChange={onContractFile} disabled={uploading} className="hidden"/>
                Replace
              </label>
            </div>
          </div>
        )}
      </div>

      {/* ── Shipping SLA ── */}
      <div className="rounded-xl border border-border bg-white p-4 grid grid-cols-1 md:grid-cols-[1fr_140px] gap-3 items-center">
        <div>
          <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Shipping SLA</div>
          <div className="text-[12.5px] text-foreground/70 mt-0.5 max-w-xl">
            How long does the seller have to ship after receiving the order? Drives the movement monitor — orders not moving past the SLA window get flagged amber, double the SLA gets flagged red.
          </div>
        </div>
        <div className="relative">
          <Input
            type="number" min="1" max="720"
            value={t.shipping_sla_hours ?? 48}
            onChange={e => update("shipping_sla_hours", e.target.value === "" ? "" : Number(e.target.value))}
            data-testid="shipping-sla"
            className="h-11 pr-14 mono text-right"
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] mono text-foreground/55">hours</span>
        </div>
      </div>

      {/* ── Returns handling ── */}
      <div className="rounded-xl border border-border bg-white p-4 space-y-3" data-testid="returns-settings">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Returns</div>
            <div className="text-[12.5px] text-foreground/70 mt-0.5 max-w-xl">
              Who handles end-customer returns? If the seller handles them, verified returns auto-credit the buyer on the next daily invoice.
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {[
            { k: "buyer_handles",  l: "Buyer handles",  sub: "All sales final for the seller. Buyer manages returns directly with their customer." },
            { k: "seller_handles", l: "Seller handles", sub: "Customer ships back to the seller. Verified returns auto-credit on next invoice." },
          ].map(opt => {
            const active = (t.returns_handler || "buyer_handles") === opt.k;
            return (
              <button key={opt.k} type="button"
                onClick={() => update("returns_handler", opt.k)}
                data-testid={`returns-handler-${opt.k}`}
                className={`text-left rounded-xl border p-3 transition-all ${active ? "border-foreground bg-foreground text-background" : "border-border bg-white hover:border-foreground/40"}`}>
                <div className="flex items-center gap-2">
                  <div className="font-medium text-[13px]">{opt.l}</div>
                  {active && <span className="ml-auto text-[9.5px] mono uppercase tracking-[0.16em] px-1.5 py-0.5 rounded-full bg-background/20">Selected</span>}
                </div>
                <div className={`text-[11.5px] mt-1.5 ${active ? "text-background/70" : "text-foreground/60"}`}>{opt.sub}</div>
              </button>
            );
          })}
        </div>

        {(t.returns_handler === "seller_handles") && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_180px] gap-3 items-center pt-2 border-t border-border">
              <div>
                <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Return window</div>
                <div className="text-[12px] text-foreground/65 mt-0.5">Days after delivery during which the buyer can file a return.</div>
              </div>
              <div className="relative">
                <Input
                  type="number" min="1" max="180"
                  value={t.return_window_days ?? 30}
                  onChange={e => update("return_window_days", e.target.value === "" ? "" : Number(e.target.value))}
                  data-testid="return-window-days"
                  className="h-11 pr-14 mono text-right"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] mono text-foreground/55">days</span>
              </div>
            </div>

            <div className="pt-2 border-t border-border">
              <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55 mb-2">
                Return address · {role === "seller" ? "your warehouse" : "seller's warehouse"}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {[
                  { k: "name", l: "Recipient / dept name", full: true },
                  { k: "line1", l: "Street address", full: true },
                  { k: "line2", l: "Suite / unit (optional)", full: true },
                  { k: "city", l: "City" },
                  { k: "state", l: "State / region" },
                  { k: "postal_code", l: "Postal code" },
                  { k: "country", l: "Country (ISO 2)" },
                  { k: "phone", l: "Phone (optional)" },
                ].map(f => (
                  <Input key={f.k}
                    value={(t.return_address || {})[f.k] || ""}
                    onChange={e => update("return_address", { ...(t.return_address || {}), [f.k]: e.target.value })}
                    placeholder={f.l}
                    data-testid={`return-addr-${f.k}`}
                    disabled={role === "buyer"}
                    className={`h-10 ${f.full ? "md:col-span-2" : ""}`}
                  />
                ))}
              </div>
              {role === "buyer" && (
                <div className="text-[11px] text-foreground/55 mt-2 italic">
                  The seller sets their return address. You'll see it here once they save it.
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* ── Shipping policy (who pays + carrier liability) ── */}
      <ShippingPolicySection t={t} role={role} update={update}/>

      {/* ── Platform fee (bilateral · both sides can flip · re-sign) ── */}
      <PlatformFeeSection t={t} role={role} update={update}/>

      {/* ── Payment policy (buyer-owned) ── */}
      <PaymentPolicySection
        t={t}
        role={role}
        posture={posture}
        paymentMethods={paymentMethods}
        update={update}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {TERM_FIELDS.map(f => (
          <FormField key={f.key} label={f.label}>
            <textarea
              value={t[f.key] || ""}
              onChange={e => update(f.key, e.target.value)}
              placeholder={f.ph}
              rows={3}
              data-testid={`term-${f.key}`}
              className="w-full rounded-md border border-border bg-white p-3 text-[13px] resize-none focus:outline-none focus:ring-2 focus:ring-foreground/10"
            />
          </FormField>
        ))}
      </div>

      {/* ── Responsibility per claim kind (replaces the old pct/flat/max) ── */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Responsibility</div>
            <div className="text-[12px] text-foreground/65 mt-0.5">
              Who covers each scenario? <strong>Seller</strong> = full deduction · <strong>Shared</strong> = 50% · <strong>Buyer</strong> = no auto-deduction (claim is informational).
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-border bg-secondary/30 overflow-hidden">
          <div className="grid grid-cols-[1fr_repeat(3,minmax(0,1fr))] gap-1 px-3 py-2 bg-secondary/50 border-b border-border text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55">
            <div>Claim kind</div><div className="text-center">Buyer</div><div className="text-center">Shared</div><div className="text-center">Seller</div>
          </div>
          {CLAIM_KINDS.map(k => (
            <div key={k.key} className="grid grid-cols-[1fr_repeat(3,minmax(0,1fr))] gap-1 px-3 py-2 items-center text-[13px] border-b border-border/50 last:border-0">
              <div>{k.label}</div>
              {["buyer", "shared", "seller"].map(who => {
                const active = (t.responsibilities?.[k.key] || "seller") === who;
                return (
                  <button
                    key={who}
                    onClick={() => setT({...t, responsibilities: {...(t.responsibilities || {}), [k.key]: who }})}
                    data-testid={`resp-${k.key}-${who}`}
                    className={`text-[12px] mono uppercase tracking-[0.14em] py-1.5 rounded-md border transition-colors ${active ? (who === "buyer" ? "bg-amber-100 text-amber-800 border-amber-300" : who === "shared" ? "bg-foreground text-background border-foreground" : "bg-emerald-100 text-emerald-800 border-emerald-300") : "border-transparent hover:bg-secondary"}`}
                  >
                    {active ? "✓ " : ""}{who}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Custom terms</div>
          <button onClick={addCustom} className="text-[12px] underline text-foreground/55 hover:text-foreground" data-testid="add-custom-term">+ Add custom term</button>
        </div>
        {(t.custom_terms || []).length === 0 && <div className="text-[12px] text-foreground/45 italic">No custom terms yet.</div>}
        <div className="space-y-2">
          {(t.custom_terms || []).map((c, i) => (
            <div key={i} className="grid grid-cols-[1fr_2fr_60px] gap-2 items-start" data-testid={`custom-term-${i}`}>
              <Input value={c.label || ""} onChange={e => updCustom(i, "label", e.target.value)} placeholder="Term label" className="h-9"/>
              <Input value={c.body || ""} onChange={e => updCustom(i, "body", e.target.value)} placeholder="Body / description" className="h-9"/>
              <button onClick={() => removeCustom(i)} className="text-[11.5px] text-rose-700 hover:bg-rose-50 rounded-md">Remove</button>
            </div>
          ))}
        </div>
      </div>

      </div>
      {/* End fields wrap */}

      <div className="flex gap-2 pt-3 border-t border-border">
        {amendMode ? (
          <Button onClick={save} disabled={saving} data-testid="submit-amendment" className="rounded-full bg-amber-600 text-white hover:bg-amber-700 h-10 px-5">
            <FileSignature className="w-4 h-4 mr-1.5"/>
            {saving ? "Submitting…" : "Submit amendment for counter-signature"}
          </Button>
        ) : isLocked ? (
          // Locked & not in amend mode — Save/Sign hidden. Banner above
          // provides the only legal action (Propose change).
          null
        ) : (
          <>
            <Button onClick={save} disabled={saving} data-testid="save-terms" variant="outline" className="rounded-full h-10 px-5">
              {saving ? "Saving…" : "Save draft"}
            </Button>
            <Button onClick={sign} disabled={youSigned || t.version === 0} data-testid="sign-terms" className="rounded-full bg-foreground text-background hover:bg-foreground/90 h-10 px-5">
              {youSigned ? "You've signed" : signLabel}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
